import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EventosPersonalesService } from '@app/core/services/eventos-personales.service';
import { EventoPersonalI } from '@app/interfaces/general/evento-personal';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
    selector: 'app-nuevo-evento',
    standalone: false,
    templateUrl: './nuevo-evento.component.html',
    styleUrl: './nuevo-evento.scss'
})
export class NuevoEventoComponent {
    private readonly fb = inject(FormBuilder);
    private readonly ref = inject(DynamicDialogRef);
    private readonly eventoService = inject(EventosPersonalesService);

    form!: FormGroup;
    mostrarPersonalizado = false;

    colores = [
        { label: 'Azul', value: '#38bdf8' },
        { label: 'Amarillo', value: '#f59e0b' },
        { label: 'Verde', value: '#10b981' },
        { label: 'Rojo', value: '#ef4444' },
        { label: 'Morado', value: '#8b5cf6' },
        { label: 'Gris', value: '#6b7280' }
    ];

    tipoRecordatorio = [
        { label: 'Sin recordatorio', value: null },
        { label: '1 día antes', value: 1440 },
        { label: '2 días antes', value: 2880 },
        { label: '1 semana antes', value: 10080 },
        { label: 'Personalizado', value: 'personalizado' }
    ];

    ngOnInit(): void {
        this.form = this.fb.group({
            titulo: ['', Validators.required],
            descripcion: [''],
            comienza: [null, Validators.required],
            fin: [null],
            todoElDia: [false],
            color: ['#38bdf8'], // por fefecto
            recordatorio: [null],
            recordatorioPersonalizado: [null]
        });

        // Cuando se activa el checkbox se ajusta 'fin' (si es null)
        this.form.get('todoElDia')?.valueChanges.subscribe((todoElDia) => {
            const comienza = this.form.get('comienza')?.value;
            if (todoElDia && comienza && !this.form.get('fin')?.value) {
                this.form.get('fin')?.setValue(comienza);
            }
        });

        // Si el checkbox se activa (con fechas null) 'fin' reacciona automáticamente a 'comienza'
        this.form.get('comienza')?.valueChanges.subscribe((comienza) => {
            const todoElDia = this.form.get('todoElDia')?.value;
            const fin = this.form.get('fin')?.value;
            if (todoElDia && comienza && !fin) {
                this.form.get('fin')?.setValue(comienza);
            }
        });
    }

    onRecordatorioChange(valor: string) {
        this.mostrarPersonalizado = valor === 'personalizado';

        if (this.mostrarPersonalizado) {
            this.form.get('recordatorioPersonalizado')?.setValidators([Validators.required]);
        } else {
            this.form.get('recordatorioPersonalizado')?.clearValidators();
            this.form.get('recordatorioPersonalizado')?.reset();
        }

        this.form.get('recordatorioPersonalizado')?.updateValueAndValidity();
    }

    guardar() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const data = this.form.value;
        const start = new Date(data.comienza);
        const end = data.fin ? new Date(data.fin) : new Date(data.comienza); // Default to start date if not provided

        if (data.todoElDia) {
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
        }

        let recordatorioMinutos: number | null = null;
        let recordatorioFecha: Date | null = null;

        // Si se selecciona personalizado, calcula los minutos
        if (data.recordatorio === 'personalizado') {
            recordatorioFecha = new Date(data.recordatorioPersonalizado);
            const diffMs = start.getTime() - recordatorioFecha.getTime();
            recordatorioMinutos = Math.max(Math.round(diffMs / 60000), 0);
        } else {
            recordatorioMinutos = data.recordatorio; // Para las otras opc
        }

        const evento: EventoPersonalI = {
            id: 0,
            activo: 1,
            titulo: data.titulo,
            descripcion: data.descripcion || '',
            fecha_inicio: start,
            fecha_fin: end,
            todo_el_dia: data.todoElDia ? 1 : 0,
            color: data.color,
            opcion_recordatorio: data.recordatorio,
            recordatorio_minutos: recordatorioMinutos,
            recordatorio_fecha: recordatorioFecha,
            fecha_hora_carga: new Date(),
            usuario: []
        };

        // Enviar al backend
        this.eventoService.insert(evento).subscribe({
            next: (res) => this.ref.close(res),
            error: (err) => {
                console.error('Error al insertar evento:', err);
                this.ref.close(null);
            }
        });
    }
}
